import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import AreaShell from '../../chrome/AreaShell';
import useV3Sync from '../../../Activations/components/hooks/useV3Sync';
import { ConfirmDialog, SectionCard } from '../../components/forms';
import { TFoot, TSkeleton, Toolbar } from '../../components/table';
import './admin-ops.css';

/*
 * v3 cloud sync: stats (polled every 60s by the hook), the global bulk
 * jobs, and per-folder repair. Every job is a GET that starts server work
 * which can't be cancelled from here. The bulk folder/media syncs hold a
 * server lock (409 while one is running); the thumbnail fix has none and
 * only answers once it has finished every video.
 */

type Job = 'folders' | 'media' | 'thumbs';

const JOBS: Record<Job, { name: string; sub: string; title: string; body: string; action: string }> = {
    folders: {
        name: 'Sync all folders',
        sub: 'Imports every unsynced v3 folder across all orgs.',
        title: 'Sync all folders?',
        body: 'Imports every unsynced folder across all orgs. It runs on the server and can’t be stopped from here.',
        action: 'Sync all folders',
    },
    media: {
        name: 'Sync all media',
        sub: 'Pulls media and shares for every active synced folder across all orgs.',
        title: 'Sync all media?',
        body: 'Pulls media and shares for every active synced folder across all orgs, one folder at a time. It can take hours, runs on the server, and can’t be stopped from here.',
        action: 'Sync all media',
    },
    thumbs: {
        name: 'Video thumbnail fix',
        sub: 'Regenerates old-style thumbnails for every video on the platform.',
        title: 'Run the video thumbnail fix?',
        body: 'Regenerates and replaces the thumbnail of every old-style video across all orgs. It has no lock, so running it twice does the work twice. The page waits until it finishes; if the request times out, the job may still be running on the server. It can’t be stopped from here.',
        action: 'Run thumbnail fix',
    },
};

/* the hook's own messages, reworded */
const FEEDBACK: Record<string, { text: string; err?: boolean }> = {
    'folders sync started': { text: 'Folder sync started on the server.' },
    'media sync started': { text: 'Media sync started on the server.' },
    'fixing thumbnails started': { text: 'Thumbnail fix finished.' },
    'repair complete and sync started': { text: 'Folder repaired; its media sync has started.' },
    'error syncing all folders': { text: 'Couldn’t start the folder sync.', err: true },
    'error syncing all media': { text: 'Couldn’t start the media sync.', err: true },
    'error fixing thumbnails': { text: 'The thumbnail fix didn’t finish.', err: true },
    'error repairing and syncing folder': { text: 'Couldn’t repair that folder.', err: true },
};

const PAGE = 50;
/* the hook has no error state for stats; a failed load just never arrives */
const GIVE_UP_MS = 20000;

const day = (iso?: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? iso.split('T')[0]
        : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};
const stamp = (iso?: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? '—'
        : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};
const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v.toLocaleString() : '—');

const errText = (e: any, job: Job) => {
    const status = e?.response?.status;
    if (status === 409) return `${JOBS[job].name} is already running.`;
    if (job === 'thumbs' && !e?.response)
        return 'The thumbnail fix request timed out or dropped. The job may still be running on the server.';
    return `Couldn’t start “${JOBS[job].name.toLowerCase()}”. Try again.`;
};

export default function AdminSyncV3() {
    const {
        isLoading,
        syncStats,
        foldersNeverSyncd,
        feedback,
        syncAllFolders,
        syncAllMedia,
        fixOldVideoThumbnails,
        repairAndSyncFolder,
    } = useV3Sync();

    /* first load: the hook's isLoading starts false and flips true in its effect */
    const [sawLoading, setSawLoading] = useState(false);
    const [settled, setSettled] = useState(false);
    useEffect(() => {
        if (isLoading) setSawLoading(true);
        else if (sawLoading) setSettled(true);
    }, [isLoading, sawLoading]);

    const [statsSlow, setStatsSlow] = useState(false);
    useEffect(() => {
        if (syncStats) return;
        const t = window.setTimeout(() => setStatsSlow(true), GIVE_UP_MS);
        return () => window.clearTimeout(t);
    }, [syncStats]);

    const [confirm, setConfirm] = useState<Job | null>(null);
    const [pending, setPending] = useState<Partial<Record<Job, boolean>>>({});
    /* after a start, hold the button until the next stats poll reports the lock */
    const [startedOn, setStartedOn] = useState<Partial<Record<Job, unknown>>>({});
    const [error, setError] = useState('');

    const run = async (job: Job) => {
        if (pending[job]) return;
        const at = syncStats;
        setPending((p) => ({ ...p, [job]: true }));
        setError('');
        try {
            if (job === 'folders') await syncAllFolders();
            else if (job === 'media') await syncAllMedia();
            else await fixOldVideoThumbnails();
            setStartedOn((s) => ({ ...s, [job]: at }));
        } catch (e) {
            setError(errText(e, job));
        } finally {
            setPending((p) => ({ ...p, [job]: false }));
        }
    };

    const running: Record<Job, boolean> = {
        folders: !!syncStats?.isBulkFolderSyncRunning,
        media: !!syncStats?.isBulkMediaSyncRunning,
        thumbs: false,
    };
    const held = (job: Job) => job in startedOn && startedOn[job] === syncStats;
    const busy = (job: Job) => !!pending[job] || running[job] || held(job);
    const label = (job: Job) =>
        pending[job]
            ? job === 'thumbs'
                ? 'Running…'
                : 'Starting…'
            : running[job]
              ? 'Running'
              : held(job)
                ? 'Started'
                : `${JOBS[job].action}…`;

    /* repair */
    const [repairing, setRepairing] = useState<V3ImportedFolder | null>(null);
    const [repairBusy, setRepairBusy] = useState<string | null>(null);
    const repair = async (f: V3ImportedFolder) => {
        if (repairBusy) return;
        setRepairBusy(`${f.portal}/${f.folder}`);
        setError('');
        try {
            await repairAndSyncFolder(f.portal, f.folder);
        } catch {
            setError(`Couldn’t repair ${f.folder}.`);
        } finally {
            setRepairBusy(null);
        }
    };

    const [query, setQuery] = useState('');
    const [limit, setLimit] = useState(PAGE);
    const q = query.trim().toLowerCase();
    const folders = useMemo(
        () =>
            (foldersNeverSyncd ?? []).filter(
                (f) => !q || `${f.folder} ${f.portal}`.toLowerCase().includes(q),
            ),
        [foldersNeverSyncd, q],
    );

    const total = syncStats?.syncedFolderCount ?? 0;
    const pct =
        syncStats && total > 0 ? `${100 - Math.round((syncStats.neverSyncedCount / total) * 100)}%` : '—';
    const fb = feedback ? FEEDBACK[feedback] ?? { text: feedback } : null;

    return (
        <AreaShell area="admin" tab="sync" sub="v3 cloud folder sync across all orgs · stats refresh every minute">
            <div className="ao-page">
                {!syncStats && statsSlow ? (
                    <p className="ao-note warn" role="status">
                        Couldn’t load sync stats yet. The page retries every minute.
                    </p>
                ) : (
                    <div className="ao-tiles" aria-busy={!syncStats}>
                        <div className="tile">
                            <p className="tl">Registered folders</p>
                            <p className="tn" style={{ margin: 0 }}>{syncStats ? num(syncStats.syncedFolderCount) : '…'}</p>
                        </div>
                        <div className="tile">
                            <p className="tl">Never synced</p>
                            <p className="tn" style={{ margin: 0 }}>{syncStats ? num(syncStats.neverSyncedCount) : '…'}</p>
                        </div>
                        <div className="tile">
                            <p className="tl">Synced</p>
                            <p className="tn" style={{ margin: 0 }}>{syncStats ? pct : '…'}</p>
                        </div>
                        <div className="tile">
                            <p className="tl">Synced automatically</p>
                            <p className="tn" style={{ margin: 0 }}>{syncStats ? num(syncStats.automaticSyncCount) : '…'}</p>
                        </div>
                        <div className="tile">
                            <p className="tl">Last sync</p>
                            <p className="tn" style={{ margin: 0, fontSize: 'var(--fs-md)' }}>
                                {syncStats ? stamp(syncStats.lastSync) : '…'}
                            </p>
                        </div>
                    </div>
                )}

                {fb && !error && (
                    <p className={fb.err ? 'ao-note err' : 'ao-note'} role="status">
                        {fb.text}
                    </p>
                )}
                {error && (
                    <p className="ao-note err" role="alert">
                        {error}
                    </p>
                )}

                <SectionCard title="Bulk jobs" note="Global · run on the server">
                    <div className="ao-jobs">
                        {(Object.keys(JOBS) as Job[]).map((job) => (
                            <div className="ao-job" key={job}>
                                <span className="ao-job-t">
                                    <span className="ao-job-n">{JOBS[job].name}</span>
                                    <span className="ao-job-s">{JOBS[job].sub}</span>
                                </span>
                                <button
                                    type="button"
                                    className="tb-btn danger-soft"
                                    disabled={busy(job)}
                                    aria-busy={!!pending[job] || running[job]}
                                    onClick={() => setConfirm(job)}
                                >
                                    {label(job)}
                                </button>
                            </div>
                        ))}
                    </div>
                </SectionCard>

                <div>
                    <SectionCard
                        title="Never synced"
                        note={settled ? `${(foldersNeverSyncd ?? []).length.toLocaleString()} folders` : undefined}
                    >
                        {(foldersNeverSyncd ?? []).length > 8 && (
                            <Toolbar
                                searchValue={query}
                                onSearch={(v) => {
                                    setQuery(v);
                                    setLimit(PAGE);
                                }}
                                searchPlaceholder="Search folder or portal"
                                filterCount={0}
                            />
                        )}
                        {!settled && (foldersNeverSyncd ?? []).length === 0 ? (
                            <TSkeleton rows={3} />
                        ) : folders.length === 0 ? (
                            <p className="quiet-empty">
                                {q ? `Nothing matches “${query}”.` : 'Every registered folder has synced at least once.'}
                            </p>
                        ) : (
                            folders.slice(0, limit).map((f) => {
                                const key = `${f.portal}/${f.folder}`;
                                return (
                                    <div className="trow2" key={f.id ?? key}>
                                        <span className="nm2" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                                            <span className="ao-mono">{f.folder}</span>
                                            <span className="sub2" style={{ display: 'block' }}>
                                                {[f.portal, day(f.date)].filter(Boolean).join(' · ')}
                                            </span>
                                        </span>
                                        <span className="right">
                                            <button
                                                type="button"
                                                className="tb-btn"
                                                disabled={repairBusy !== null}
                                                aria-label={`Repair and sync ${f.folder}`}
                                                onClick={() => setRepairing(f)}
                                            >
                                                {repairBusy === key ? 'Repairing…' : 'Repair…'}
                                            </button>
                                        </span>
                                    </div>
                                );
                            })
                        )}
                    </SectionCard>
                    {folders.length > limit && (
                        <TFoot>
                            <span className="ao-more">
                                <span>
                                    {limit.toLocaleString()} of {folders.length.toLocaleString()}
                                </span>
                                <button type="button" className="tb-btn" onClick={() => setLimit((l) => l + PAGE)}>
                                    Show more
                                </button>
                            </span>
                        </TFoot>
                    )}
                </div>
            </div>

            {confirm && (
                <ConfirmDialog
                    open
                    title={JOBS[confirm].title}
                    body={JOBS[confirm].body}
                    actionLabel={JOBS[confirm].action}
                    onConfirm={() => run(confirm)}
                    onClose={() => setConfirm(null)}
                />
            )}
            <ConfirmDialog
                open={repairing !== null}
                danger={false}
                title={`Repair ${repairing?.folder ?? 'folder'}?`}
                body={`Re-registers ${repairing?.folder ?? 'this folder'} from portal ${repairing?.portal ?? '—'} if it’s missing, then starts syncing its media. Runs on the server and can’t be stopped from here.`}
                actionLabel="Repair and sync"
                onConfirm={() => repairing && repair(repairing)}
                onClose={() => setRepairing(null)}
            />
        </AreaShell>
    );
}
